import React, { useState, useReducer } from 'react';

import './Products.list.scss';
import { BaseReact } from '../../base.model';
import { ProfileQL } from '../../models/Profile';

import { NavLink } from 'react-router-dom';
import SectionTitleBar from '../components/titlebar';
import { Subject } from 'rxjs';
import { AmazonItemsBinded, AmazonItemBinder } from '../../models/Items';
import { useQuery, useMutation } from '@apollo/client';
import { graphqlSchema } from '../../services/graphql.schema';

import { numberToCurrencyString } from '../../services/formatting';
import ItemEditModal from './Modals/Product.edit';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import Pagination from '../Forms/Pagination';
import InputCard from '../Forms/Input.cardForm';
import SelectCard from '../Forms/Select.cardForm';

interface ProductsProps extends BaseReact {
    currentUser: ProfileQL;
}

const ENTRIES_PER_PAGE = 35;

function itemsReducer(state: { items: AmazonItemsBinded[] }, action: { type: string, payload: AmazonItemsBinded[] }) {
    switch (action.type) {
        case 'set':
            return Object.assign({}, { items: action.payload });
        case 'update':
            return Object.assign({}, { items: action.payload });
        default:
            return Object.assign({}, { items: action.payload });
    }
}

function ProductsList({ history, location, currentUser }: ProductsProps) {
    const [searchSubject] = useState(new Subject<string>());
    const [page, setPage] = useState(1);

    const [search, setSearch] = useState('');
    const [customFilter, setCustomFilter] = useState<'all' | 'configured' | 'un_configured'>('all');

    const [status, setStatus] = useState<{ fetchedAt: number; textStatus: string; }>(null)
    const [isLoading, setIsLoading] = useState(true);
    const [items, dispatchItems] = useReducer(itemsReducer, { items: [] } as { items: AmazonItemsBinded[] });

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<AmazonItemsBinded>(null);

    const [setBinder] = useMutation(graphqlSchema.PROFILE.ITEMS.setItemBinder, {
        onCompleted: ({ setItemBinder }) => {
            console.log('Updated Item Binder: ', setItemBinder);
            // Update product object
            const _items: { items: AmazonItemsBinded[] } = JSON.parse(JSON.stringify(items))
            const itemToBind = _items.items.find(it => it.item.productId === setItemBinder.productId);
            if (itemToBind) {
                itemToBind.binder = setItemBinder;
                dispatchItems({ type: 'update', payload: _items.items });
            }
            sharedToasterSubject.next({ type: 'confirm', message: 'Se actualizó el producto' });
        },
        onError: (e) => {
            console.error('Error updating binder: ', e);
            sharedToasterSubject.next({ type: 'error', message: 'Error al actualizar el producto, comuníquese con soporte' });
        }
    });


    const saveOnClose = (save: boolean, binder: AmazonItemBinder) => {
        setModalIsOpen(false);
        console.log('Saving Binder: ', save);
        console.log('Binder: ', binder);
        if (save && !!binder) {
            setBinder({
                variables: {
                    binder
                }
            })
        }
    }

    useQuery(graphqlSchema.PROFILE.ITEMS.getItemStatus, {
        onCompleted: ({ getItemStatus }: { getItemStatus: { fetchedAt: number; textStatus: string; } }) => {
            console.log('Get Item Status: ', getItemStatus);
            setStatus(getItemStatus);
        },
        onError: (e) => {
            console.error('E: ', e);
        },
        fetchPolicy: 'cache-and-network'
    })
    useQuery(graphqlSchema.PROFILE.ITEMS.getItems, {
        onCompleted: ({ getItems }: { getItems: AmazonItemsBinded[] }) => {
            console.log('Get Items: ', getItems);
            dispatchItems({ type: 'set', payload: getItems });
            setIsLoading(false)
        },
        onError: (e) => {
            console.error('E: ', e);
        },
        fetchPolicy: 'cache-and-network'
    })

    const handleSearch = (value: string) => {
        setSearch(value);
        searchSubject.next(value);
    }

    const selectItemForEdit = (item: AmazonItemsBinded) => {
        return () => {
            setSelectedItem(item);
            setModalIsOpen(true);
        }
    }

    const changePage = (_page: number) => {
        console.log('Changing page to: ', _page);
        setPage(_page);
    }

    const _filterItemsByState = () => {
        const searchCase = !search ? items.items : items.items.filter(item => {
            const words = search.toLowerCase().split(' ');
            const nameWords = item.item.itemName.toLowerCase().split(' ');
            const id = item.item.asin1.toUpperCase();
            const idCheck = search.toUpperCase();
            return words.every(w => nameWords.some(nW => nW.includes(w))) || id.includes(idCheck);
        });
        if (customFilter === 'all') {
            return searchCase;
        }
        else if (customFilter === 'configured') {
            return searchCase.filter(c => !!c.binder);
        } else {
            return searchCase.filter(c => !c.binder);
        }
    }

    const renderNoItemsYet = () => {
        if (!(isLoading) && !!items && !items.items.length) {
            return (
                <div className='_isEmpty'>
                    <h4>
                        ¡No encontramos productos a mostrar!
                    </h4>
                    <p>
                        Una vez se agreguen productos a vender los verás en esta vista, donde podrás configurar sus claves SAT e impuestos.
                    </p>
                </div>
            )
        } else {
            return null;
        }
    }

    const itemFilterOptions = [
        { value: 'all', label: 'Todos' },
        { value: 'configured', label: 'Con Claves' },
        { value: 'un_configured', label: 'Sin Claves' },
    ]

    const _filteredItems = _filterItemsByState();
    const maxPage = Math.ceil(_filteredItems.length / ENTRIES_PER_PAGE);
    const _page = page <= maxPage ? page : maxPage;
    const filteredItems = _filteredItems.filter((_, i) => (i >= ENTRIES_PER_PAGE * (_page - 1)) && (i < ENTRIES_PER_PAGE * _page));

    return (
        <div id="ItemsList">
            <SectionTitleBar currentUser={currentUser} title="Tus productos" />
            <div className="itemsContent">
                <div className='card'>
                    <div className="toBulkEdit show-md">
                        <NavLink to="/items/bulk">
                            <span>
                                Editar múltiples listados
                            </span>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                            </svg>
                        </NavLink>
                    </div>

                    <div className='searchBlock'>
                        <InputCard type="text" icon='search' value={search} onChange={handleSearch} label={'Buscar por nombre / ID'} placeholder="Busca tus productos por nombre o ID" />
                        <div className="stats">
                            <p className="stats">
                                {
                                    status ?
                                        status.textStatus : ''
                                }
                            </p>
                        </div>
                    </div>
                    <div className="filterChange">
                        <SelectCard value={customFilter} onChange={setCustomFilter} label={'Personalización'} options={itemFilterOptions} />
                        <div className="toBulkEdit">

                            <NavLink to="/items/bulk">
                                <span>
                                    Editar múltiples listados
                                </span>
                                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                </svg>
                            </NavLink>
                        </div>
                    </div>

                    <div className="itemList">
                        <div className="item header">
                            <p className="_id">
                                ID / SKU
                            </p>
                            <p>
                                Nombre
                            </p>
                            <p className="_price">
                                Precio
                            </p>
                            <p className="_uuid">
                                Claves SAT
                            </p>
                            <p className="_taxes">
                                Impuestos
                            </p>
                        </div>
                        {
                            filteredItems.map((item) => {
                                const defaultClave = currentUser.amazonProfile.defaultClaveSatProdServ || null;
                                const defaultUnidad = currentUser.amazonProfile.defaultUnidadSat || null;

                                const prodClave = !!item.binder && item.binder.claveProdServ ? item.binder.claveProdServ : (defaultClave || '--');
                                const prodUnidad = !!item.binder && item.binder.claveUnidad ? item.binder.claveUnidad : (defaultUnidad || '--');
                                return (
                                    <div className="item" key={item.item.listingId}>
                                        <div className="_id">
                                            <p className="_id">
                                                {item.item.productId}
                                            </p>
                                            <p className="_sku">
                                                {item.item.sellerSku}
                                            </p>
                                        </div>
                                        <div className='_name'>
                                            <p className="_name">
                                                {item.item.itemName.substring(0, 45)}
                                            </p>
                                            {
                                                item.binder && item.binder.pedimento ? (
                                                    <p className={`_status ${item.binder.pedimento}`}>
                                                        Pedimento: {item.binder.pedimento}
                                                    </p>
                                                ) : null
                                            }
                                        </div>
                                        <div className='_price'>
                                            <p className="_price">
                                                $ {numberToCurrencyString(item.item.price)}
                                            </p>
                                        </div>
                                        <div className='_clave'>
                                            <p className={`_clave${!!item.binder ? ' customized' : ''}`}>
                                                {prodClave}
                                            </p>
                                            <p className="_clave">
                                                {prodUnidad}
                                            </p>
                                        </div>
                                        <div className='_taxes'>
                                            {
                                                !item.binder || (!!item.binder && !item.binder.taxes.length) ? (
                                                    <p className="_taxes">
                                                        IVA 16%
                                                    </p>
                                                ) : (
                                                    item.binder.taxes.map((t, i) => (
                                                        <p className="_taxes" key={i}>
                                                            {t.type.toUpperCase()} {t.tasa * 100}%
                                                        </p>
                                                    ))
                                                )
                                            }
                                        </div>
                                        <button onClick={selectItemForEdit(item)} className="svg mq4">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                            </svg>
                                        </button>
                                    </div>
                                )
                            })
                        }

                        {renderNoItemsYet()}
                    </div>
                    {
                        items && items.items.length ?
                            <Pagination page={_page} pages={Math.ceil(_filteredItems.length / ENTRIES_PER_PAGE)} onPage={changePage} />
                            : ''
                    }
                </div>
            </div>
            {ItemEditModal({ modalIsOpen, item: selectedItem, onClose: saveOnClose, authToken: currentUser.amazonProfile.authToken })}
        </div>
    )
}


export default ProductsList;